import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Card,
  Stack,
  Typography,
  Button,
  Divider,
  Modal,
  CircularProgress,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { WarningOutlined } from '@mui/icons-material';
import { toast } from 'react-toastify';
import Scrollbar from 'src/components/scrollbar/Scrollbar';
import { HotTable } from '@handsontable/react';
import Handsontable from 'handsontable';
import { registerAllModules } from 'handsontable/registry';
import 'handsontable/dist/handsontable.full.min.css';
import api from 'src/services/BaseService';
import './pointsBulkEditPage.css';

registerAllModules();

const ECOMMERCE_COMPANIES = [
  {
    label: 'Trendyol',
    idField: 'trendyolID',
    idEditable: false,
    sendId: false,
    enableField: 'isTrendyol',
    hasCalismaDurumu: true,
    allowPasifeAlma: false,
    activeField: 'isActiveOnTrendyol',
    activeType: 'flag',
  },
  {
    label: 'Hepsiburada',
    idField: 'branchCode',
    idEditable: false,
    sendId: true,
    enableField: 'isHepsiburada',
    hasCalismaDurumu: true,
    allowPasifeAlma: false,
    activeField: 'isActiveOnHepsiburada',
    activeType: 'flag',
  },
  {
    label: 'CargoX',
    idField: 'kargoistID',
    idEditable: true,
    sendId: true,
    enableField: 'isKargoist',
    hasCalismaDurumu: true,
    allowPasifeAlma: false,
    activeField: 'isActiveOnKargoist',
    activeType: 'flag',
  },
  {
    label: 'Hepsijet',
    idField: 'hepsijetID',
    idEditable: true,
    sendId: true,
    enableField: 'isHepsijet',
    hasCalismaDurumu: true,
    allowPasifeAlma: false,
    activeField: 'isActiveOnHepsijet',
    activeType: 'flag',
  },
  {
    label: 'Aras Kargo',
    idField: 'arasKargoID',
    idEditable: true,
    sendId: true,
    enableField: null,
    hasCalismaDurumu: true,
    allowPasifeAlma: false,
    activeField: 'isArasKargo',
    activeType: 'bool',
  },
  {
    label: 'Sürat Kargo',
    idField: null,
    idEditable: false,
    sendId: false,
    enableField: null,
    hasCalismaDurumu: false,
    allowPasifeAlma: true,
    activeField: 'isSuratKargo',
    activeType: 'bool',
  },
];

const CALISMA_DURUMU_OPTIONS = ['Açık', 'Kapalı'];
const ISLEM_OPTIONS = ['Aktif Etme', 'Pasife Alma'];
const NOKTA_BULUNAMADI_TEXT = 'Nokta bulunamadı.';
const CALISMA_DURUMU_YOK_TEXT = '-';
const TOTAL_ROWS = 30;

const getColumnHeaders = () => [
  'No',
  'Nokta ID',
  'Nokta Adı',
  'E-Ticaret Firması',
  'Firma ID',
  'İşlem',
  'Çalışma Durumu',
];

export default function PointsBulkEditPage() {
  const calculatedHeight = window.innerHeight - 220;
  const headers = getColumnHeaders();
  const noktaIdCol = headers.indexOf('Nokta ID');
  const noktaAdiCol = headers.indexOf('Nokta Adı');
  const firmaCol = headers.indexOf('E-Ticaret Firması');
  const firmaIdCol = headers.indexOf('Firma ID');
  const islemCol = headers.indexOf('İşlem');
  const calismaDurumuCol = headers.indexOf('Çalışma Durumu');

  const buildInitialRows = () =>
    Array.from({ length: TOTAL_ROWS }, () =>
      Array.from({ length: headers.length }, () => '')
    );

  const [tableData, setTableData] = useState(buildInitialRows);
  const [saving, setSaving] = useState(false);
  const [pointsLoading, setPointsLoading] = useState(true);
  const [progressOpen, setProgressOpen] = useState(false);
  const [progressRows, setProgressRows] = useState([]);
  const hotRef = useRef(null);
  const pointsByIdRef = useRef({});

  useEffect(() => {
    if (hotRef.current) {
      hotRef.current.hotInstance.loadData(tableData);
    }
  }, [tableData]);

  useEffect(() => {
    let loadedFromHandoff = false;

    try {
      const raw = localStorage.getItem('easypoint_bulk_edit_points');
      if (raw) {
        const parsedPoints = JSON.parse(raw);
        if (Array.isArray(parsedPoints) && parsedPoints.length > 0) {
          const map = {};
          parsedPoints.forEach((point) => {
            map[String(point.id)] = point;
          });
          pointsByIdRef.current = map;
          loadedFromHandoff = true;
        }
      }
      localStorage.removeItem('easypoint_bulk_edit_points');
    } catch (error) {
      // Bozuk/erişilemeyen veri olsa da servisten çekmeye devam edeceğiz.
    }

    if (loadedFromHandoff) {
      setPointsLoading(false);
    } else {
      api
        .post('flow/get-points')
        .then((response) => {
          if (response.data.status) {
            const map = {};
            (response.data.result || []).forEach((point) => {
              map[String(point.id)] = point;
            });
            pointsByIdRef.current = map;
          } else {
            toast.error(response.data.message);
          }
        })
        .catch((error) => toast.error(error.message))
        .finally(() => setPointsLoading(false));
    }
  }, []);

  const getColumnSettings = () =>
    headers.map((header) => {
      if (header === 'No') {
        return {
          data: null,
          readOnly: true,
          className: 'htCenter htMiddle',
          renderer: (instance, td, row) => {
            Handsontable.dom.empty(td);
            Handsontable.dom.fastInnerHTML(td, row + 1);
            td.className = 'htCenter htMiddle';
            return td;
          },
        };
      }

      if (header === 'Nokta ID') {
        return { type: 'text', className: 'htCenter htMiddle' };
      }

      if (header === 'Nokta Adı') {
        return {
          type: 'text',
          readOnly: true,
          className: 'htCenter htMiddle',
        };
      }

      if (header === 'E-Ticaret Firması') {
        return {
          type: 'dropdown',
          strict: true,
          allowInvalid: false,
          className: 'htCenter htMiddle',
          source: ECOMMERCE_COMPANIES.map((company) => company.label),
        };
      }

      if (header === 'Firma ID') {
        return { type: 'text', readOnly: true, className: 'htCenter htMiddle' };
      }

      if (header === 'İşlem') {
        return {
          type: 'dropdown',
          strict: true,
          allowInvalid: false,
          readOnly: true,
          className: 'htCenter htMiddle',
          source: ISLEM_OPTIONS,
        };
      }

      if (header === 'Çalışma Durumu') {
        return {
          type: 'dropdown',
          strict: true,
          allowInvalid: false,
          readOnly: true,
          className: 'htCenter htMiddle',
          source: CALISMA_DURUMU_OPTIONS,
        };
      }

      return { type: 'text', className: 'htCenter htMiddle' };
    });

  // Belirli bir satırdaki Nokta ID + seçili firmaya göre noktayı ve firma ayarını bulur.
  const resolveRowContext = (hotInstance, row) => {
    const noktaIdValue = (hotInstance.getDataAtCell(row, noktaIdCol) || '')
      .toString()
      .trim();
    const firmaLabel = hotInstance.getDataAtCell(row, firmaCol);
    const point = noktaIdValue ? pointsByIdRef.current[noktaIdValue] : null;
    const company = ECOMMERCE_COMPANIES.find(
      (item) => item.label === firmaLabel
    );
    return { noktaIdValue, point, company };
  };

  const cellsFn = (row, col) => {
    const cellProperties = {};
    const hotInstance = hotRef.current?.hotInstance;
    if (!hotInstance) return cellProperties;

    if (col === noktaIdCol) {
      if (pointsLoading) {
        cellProperties.readOnly = true;
        cellProperties.placeholder = 'Noktalar yükleniyor...';
        cellProperties.className = 'htCenter htMiddle';
      } else {
        const noktaIdValue = (hotInstance.getDataAtCell(row, noktaIdCol) || '')
          .toString()
          .trim();
        const isInvalid = noktaIdValue && !pointsByIdRef.current[noktaIdValue];
        cellProperties.readOnly = false;
        cellProperties.placeholder = '';
        cellProperties.className = isInvalid
          ? 'htCenter htMiddle bulk-edit-invalid-cell'
          : 'htCenter htMiddle';
      }
    }

    if (col === firmaIdCol) {
      const { company } = resolveRowContext(hotInstance, row);
      if (company && company.idEditable) {
        cellProperties.readOnly = false;
        cellProperties.placeholder = '';
      } else {
        cellProperties.readOnly = true;
        cellProperties.placeholder = company
          ? company.idField
            ? 'Bu firma ID otomatik atanır'
            : 'Bu firmada Firma ID yok'
          : '';
      }
    }

    if (col === islemCol) {
      const { company } = resolveRowContext(hotInstance, row);
      if (company) {
        cellProperties.readOnly = false;
        cellProperties.source = company.allowPasifeAlma
          ? ISLEM_OPTIONS
          : ['Aktif Etme'];
      } else {
        cellProperties.readOnly = true;
        cellProperties.source = ISLEM_OPTIONS;
      }
    }

    if (col === calismaDurumuCol) {
      const { company } = resolveRowContext(hotInstance, row);
      if (!company) {
        cellProperties.readOnly = true;
        cellProperties.placeholder = '';
      } else if (!company.hasCalismaDurumu) {
        // Sürat Kargo'da Çalışma Durumu kavramı yok.
        cellProperties.readOnly = true;
        cellProperties.placeholder = CALISMA_DURUMU_YOK_TEXT;
      } else {
        cellProperties.readOnly = false;
        cellProperties.placeholder = '';
      }
    }

    return cellProperties;
  };

  const onAfterChange = (changes, source) => {
    if (!hotRef.current || source === 'setDataAtCell' || source === 'loadData')
      return;
    const hotInstance = hotRef.current.hotInstance;
    const dataToSet = [];
    const rowsToRefresh = new Set();

    changes?.forEach(([row, col, prevValue, newValue]) => {
      if (col === noktaIdCol || col === firmaCol) {
        rowsToRefresh.add(row);
      }

      if (col === firmaIdCol && typeof newValue === 'string' && newValue) {
        const normalized = newValue
          .toLocaleUpperCase('tr-TR')
          .replace(/\s+/g, '');
        if (normalized !== newValue) {
          dataToSet.push([row, firmaIdCol, normalized]);
        }
      }
    });

    rowsToRefresh.forEach((row) => {
      const { noktaIdValue, point, company } = resolveRowContext(
        hotInstance,
        row
      );

      if (!noktaIdValue) {
        dataToSet.push([row, noktaAdiCol, '']);
      } else {
        dataToSet.push([
          row,
          noktaAdiCol,
          point ? point.title || '' : NOKTA_BULUNAMADI_TEXT,
        ]);
      }

      if (company) {
        const existingId =
          company.idField && point ? point[company.idField] || '' : '';
        dataToSet.push([row, firmaIdCol, existingId]);
        // Diğer tüm firmalarda İşlem yalnızca "Aktif Etme" olabildiği için otomatik seçiyoruz;
        // Sürat Kargo'da iki seçenek de geçerli olduğundan kullanıcı seçimine bırakıyoruz.
        dataToSet.push([
          row,
          islemCol,
          company.allowPasifeAlma ? '' : 'Aktif Etme',
        ]);
        dataToSet.push([
          row,
          calismaDurumuCol,
          company.hasCalismaDurumu ? '' : CALISMA_DURUMU_YOK_TEXT,
        ]);
      } else {
        dataToSet.push([row, firmaIdCol, '']);
        dataToSet.push([row, islemCol, '']);
        dataToSet.push([row, calismaDurumuCol, '']);
      }
    });

    if (dataToSet.length > 0) {
      hotInstance.setDataAtCell(dataToSet, 'setDataAtCell');
    }
  };

  const buildRowPlan = (currentData) => {
    const plan = [];

    currentData.forEach((row, rowIndex) => {
      const noktaId = (row[noktaIdCol] || '').toString().trim();
      const firmaLabel = row[firmaCol];
      const firmaIdValue = (row[firmaIdCol] || '').toString().trim();
      const islem = row[islemCol];
      const calismaDurumu = row[calismaDurumuCol];

      const rowHasData =
        noktaId || firmaLabel || firmaIdValue || islem || calismaDurumu;
      if (!rowHasData) return;

      const point = pointsByIdRef.current[noktaId];
      const company = ECOMMERCE_COMPANIES.find(
        (item) => item.label === firmaLabel
      );
      const label = `${rowIndex + 1}. satır — ${
        point?.title || noktaId || 'Nokta ID yok'
      }${firmaLabel ? ` — ${firmaLabel}` : ''}`;

      if (!point) {
        plan.push({
          rowIndex,
          label,
          status: 'skipped',
          message: 'Nokta bulunamadı',
        });
        return;
      }
      if (!company) {
        plan.push({
          rowIndex,
          label,
          status: 'skipped',
          message: 'Firma seçilmedi',
        });
        return;
      }
      if (!islem) {
        plan.push({
          rowIndex,
          label,
          status: 'skipped',
          message: 'İşlem seçilmedi',
        });
        return;
      }
      if (company.idEditable && !point[company.idField] && !firmaIdValue) {
        plan.push({
          rowIndex,
          label,
          status: 'skipped',
          message: 'Firma ID girilmedi',
        });
        return;
      }
      if (company.hasCalismaDurumu && !calismaDurumu) {
        plan.push({
          rowIndex,
          label,
          status: 'skipped',
          message: 'Çalışma Durumu seçilmedi',
        });
        return;
      }

      const isActive = company.hasCalismaDurumu
        ? calismaDurumu === 'Açık'
        : islem === 'Aktif Etme';
      const payload = { id: point.id };

      if (company.idField && company.sendId) {
        payload[company.idField] = firmaIdValue;
      }

      if (company.enableField) {
        payload[company.enableField] = true;
      }
      payload[company.activeField] =
        company.activeType === 'bool' ? isActive : isActive ? '1' : '0';

      plan.push({ rowIndex, label, status: 'pending', payload });
    });

    return plan;
  };

  const updateProgressRow = (index, patch) => {
    setProgressRows((prev) =>
      prev.map((item, i) => (i === index ? { ...item, ...patch } : item))
    );
  };

  const handleSave = async () => {
    const hotInstance = hotRef.current.hotInstance;
    const currentData = hotInstance.getData();
    const plan = buildRowPlan(currentData);

    if (plan.length === 0) {
      toast.error('İşlenecek satır bulunamadı.');
      return;
    }

    setProgressRows(plan);
    setProgressOpen(true);
    setSaving(true);

    for (let i = 0; i < plan.length; i += 1) {
      if (plan[i].status !== 'pending') continue;

      updateProgressRow(i, { status: 'processing' });

      try {
        // eslint-disable-next-line no-await-in-loop
        const response = await api.post(
          '/easypoint-centers/update',
          plan[i].payload
        );
        if (response.data.status) {
          updateProgressRow(i, { status: 'success' });
        } else {
          updateProgressRow(i, {
            status: 'error',
            message: response.data.message || 'Güncellenemedi',
          });
        }
      } catch (error) {
        updateProgressRow(i, {
          status: 'error',
          message: error.message || 'Servis hatası',
        });
      }
    }

    setSaving(false);
  };

  const handleCloseProgress = () => {
    if (saving) return;

    const hotInstance = hotRef.current?.hotInstance;
    if (hotInstance) {
      const currentData = hotInstance.getData();
      const rowsToKeep = progressRows
        .filter((item) => item.status !== 'success')
        .map((item) => currentData[item.rowIndex]);

      const newData = [...rowsToKeep];
      while (newData.length < TOTAL_ROWS) {
        newData.push(Array.from({ length: headers.length }, () => ''));
      }
      setTableData(newData);
    }

    setProgressOpen(false);
    setProgressRows([]);
  };

  return (
    <>
      <Helmet>
        <title>Toplu Düzenleme | Easy Point Panel</title>
      </Helmet>
      <Stack style={{ paddingInline: '25px', paddingTop: '25px' }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
        >
          <Typography variant="h4" gutterBottom>
            Toplu Düzenleme
          </Typography>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving}
            endIcon={
              saving ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SendIcon />
              )
            }
          >
            Kaydet
          </Button>
        </Stack>

        <Card>
          <Scrollbar>
            <Box sx={{ width: '100%' }}>
              <HotTable
                ref={hotRef}
                rowHeights={35}
                licenseKey="non-commercial-and-evaluation"
                data={tableData}
                persistentState={true}
                colHeaders={headers}
                columns={getColumnSettings()}
                cells={cellsFn}
                afterChange={onAfterChange}
                stretchH="all"
                height={calculatedHeight}
              />
            </Box>
          </Scrollbar>
        </Card>
      </Stack>

      <Modal
        open={progressOpen}
        onClose={() => !saving && handleCloseProgress()}
      >
        <Box
          sx={{
            position: 'absolute',
            width: 520,
            maxWidth: '90vw',
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.paper',
            borderRadius: '10px',
            boxShadow: 24,
            p: 3,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            {saving ? 'Toplu Düzenleme İşleniyor...' : 'Toplu Düzenleme Sonucu'}
          </Typography>

          <Stack spacing={1} sx={{ overflowY: 'auto', pr: 1 }}>
            {progressRows.map((item, index) => (
              <Stack
                key={index}
                direction="row"
                alignItems="flex-start"
                spacing={1.5}
                sx={{ p: 1, borderRadius: '6px', backgroundColor: '#F9FAFB' }}
              >
                <Box sx={{ width: 20, height: 20, flexShrink: 0, mt: '2px' }}>
                  {item.status === 'pending' && (
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        border: '2px solid #D1D5DB',
                        margin: '3px',
                      }}
                    />
                  )}
                  {item.status === 'processing' && (
                    <CircularProgress size={18} />
                  )}
                  {item.status === 'success' && (
                    <CheckCircleIcon sx={{ color: '#12B76A', fontSize: 20 }} />
                  )}
                  {item.status === 'error' && (
                    <WarningOutlined sx={{ color: '#D92D20', fontSize: 20 }} />
                  )}
                  {item.status === 'skipped' && (
                    <Typography
                      sx={{
                        color: '#98A2B3',
                        fontSize: 14,
                        textAlign: 'center',
                      }}
                    >
                      —
                    </Typography>
                  )}
                </Box>
                <Stack sx={{ flex: 1 }}>
                  <Typography variant="body2">{item.label}</Typography>
                  {item.status === 'skipped' && (
                    <Typography variant="caption" sx={{ color: '#98A2B3' }}>
                      Atlandı: {item.message}
                    </Typography>
                  )}
                  {item.status === 'error' && (
                    <Typography variant="caption" color="error">
                      {item.message}
                    </Typography>
                  )}
                  {item.status === 'success' && (
                    <Typography variant="caption" sx={{ color: '#12B76A' }}>
                      Başarılı
                    </Typography>
                  )}
                </Stack>
              </Stack>
            ))}
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="body2" sx={{ color: '#6B7280' }}>
              Başarılı:{' '}
              {progressRows.filter((r) => r.status === 'success').length} ·
              Hatalı: {progressRows.filter((r) => r.status === 'error').length}{' '}
              · Atlandı:{' '}
              {progressRows.filter((r) => r.status === 'skipped').length}
            </Typography>
            <Button
              disabled={saving}
              variant="contained"
              onClick={handleCloseProgress}
            >
              Kapat
            </Button>
          </Stack>
        </Box>
      </Modal>
    </>
  );
}
